import type { CSSProperties } from 'react'
import type { Role } from '@lms/shared-types'
import { roleAtLeast } from '@lms/shared-types'
import { Modal } from '../primitives/Modal'
import { isMacPlatform, shortcutModifierLabel } from '../../lib/platform'

interface ShortcutsHelpModalProps {
  open: boolean
  onClose: () => void
  role: Role
}

interface ShortcutEntry {
  keys: string
  label: string
  /** Minimum role required to see this shortcut. Omit = always visible. */
  minimumRole?: Role
}

/**
 * Build the shortcut rows for the current platform. Navigation / help chords
 * use the platform modifier (⌘ on macOS, Alt on Windows); the palette uses
 * ⌘ / Ctrl.
 */
function buildShortcuts(): ReadonlyArray<ShortcutEntry> {
  const mod = shortcutModifierLabel() // '⌘' or 'Alt'
  const paletteMod = isMacPlatform() ? '⌘' : 'Ctrl'
  return [
    { keys: `${paletteMod} K`, label: 'Command palette' },
    { keys: `${mod} /`, label: 'This help' },
    { keys: 'Esc', label: 'Close overlay' },
    { keys: `${mod} H`, label: 'Go to home' },
    { keys: `${mod} R`, label: 'Go to requests', minimumRole: 'manager' },
    { keys: `${mod} C`, label: 'Go to calendar' },
    { keys: `${mod} A`, label: 'Apply for leave' },
    { keys: `${mod} I`, label: 'Go to insights', minimumRole: 'hr' },
    { keys: `${mod} E`, label: 'Go to employees', minimumRole: 'hr' },
    { keys: `${mod} D`, label: 'Go to dashboard', minimumRole: 'manager' },
  ]
}

export function ShortcutsHelpModal({
  open,
  onClose,
  role,
}: Readonly<ShortcutsHelpModalProps>): JSX.Element | null {
  const visible = buildShortcuts().filter(
    (s) => s.minimumRole == null || roleAtLeast(role, s.minimumRole),
  )

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Keyboard shortcuts"
      footer={
        <p style={footerNoteStyle}>
          Hold {shortcutModifierLabel()} plus the key. Shortcuts work
          everywhere except inside text inputs.
        </p>
      }
    >
      <div style={gridStyle}>
        {visible.map((shortcut) => (
          <ShortcutRow key={shortcut.keys} entry={shortcut} />
        ))}
      </div>
    </Modal>
  )
}

interface ShortcutRowProps {
  entry: ShortcutEntry
}

function ShortcutRow({ entry }: Readonly<ShortcutRowProps>): JSX.Element {
  return (
    <>
      <kbd style={kbdStyle} className="wf-mono">
        {entry.keys}
      </kbd>
      <span style={labelStyle}>{entry.label}</span>
    </>
  )
}

// ── Styles ────────────────────────────────────────────────────────────────────

const gridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'auto 1fr',
  columnGap: 16,
  rowGap: 10,
  alignItems: 'center',
  paddingTop: 8,
}

const kbdStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 56,
  padding: '3px 8px',
  fontSize: 11,
  color: 'var(--text-2)',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 5,
  boxShadow: '0 1px 0 var(--line-strong)',
  whiteSpace: 'nowrap',
  letterSpacing: '0.04em',
  justifySelf: 'start',
}

const labelStyle: CSSProperties = {
  fontSize: 13,
  color: 'var(--text)',
}

const footerNoteStyle: CSSProperties = {
  fontSize: 11,
  color: 'var(--text-3)',
  margin: 0,
  fontFamily: 'var(--font-sans)',
}
